/**
 * NavmeshSystem — continuous free-form pathfinding via the `navmesh` library.
 *
 * buildPolysFromGridMap merges adjacent walkable tiles into convex rectangular
 * hulls, so the navmesh has far fewer polygons than one-per-tile approaches.
 * findPath returns world-pixel waypoints (not tile coordinates).
 */

import { NavMesh, buildPolysFromGridMap } from 'navmesh';

export interface WorldPoint { x: number; y: number; }

export class NavmeshSystem {
  private navMesh: NavMesh | null = null;

  /**
   * Build (or rebuild) the navmesh from a walkability grid.
   * Call once at level load, and again after any dynamic obstacle change.
   *
   * @param walkabilityGrid  0 = walkable, 1 = blocked (same as EasyStar grid)
   * @param tileSize         world-pixel size of each grid cell (default 32)
   */
  build(walkabilityGrid: number[][], tileSize = 32): void {
    try {
      // buildPolysFromGridMap merges walkable tiles into convex hull rectangles.
      // The resulting polygons are already in world-pixel space.
      const polys = buildPolysFromGridMap(
        walkabilityGrid,
        tileSize,
        tileSize,
        (value: number) => value === 0,  // 0 = walkable
      );
      this.navMesh = polys.length > 0 ? new NavMesh(polys) : null;
      if (!this.navMesh) {
        console.warn('NavmeshSystem: grid produced no walkable polygons');
      }
    } catch (e) {
      console.warn('NavmeshSystem: build failed —', e);
      this.navMesh = null;
    }
  }

  /**
   * Find a world-pixel path from `from` to `to`.
   * Returns the full path including both endpoints, or null if unreachable.
   *
   * If either endpoint is outside the navmesh (e.g. a soldier nudged into a
   * wall by physics), we snap it to the nearest valid mesh point before
   * pathfinding so that physics-pushed positions don't produce null paths.
   */
  findPath(from: WorldPoint, to: WorldPoint): WorldPoint[] | null {
    if (!this.navMesh) return null;
    const snappedFrom = this.snapToMesh(from);
    const snappedTo   = this.snapToMesh(to);
    if (!snappedFrom || !snappedTo) return null;
    return (this.navMesh.findPath(snappedFrom, snappedTo) as WorldPoint[] | null) ?? null;
  }

  /**
   * If the point is already inside the navmesh, return it unchanged.
   * Otherwise project it onto the nearest mesh edge (up to 2 tile widths away).
   *
   * `findClosestMeshPoint` internally calls `.distance()` (and other Vector2 methods)
   * on the passed object, so a plain {x,y} literal crashes at runtime.
   * We build a minimal duck-type shim with all the methods the navmesh library uses.
   */
  private snapToMesh(point: WorldPoint): WorldPoint | null {
    if (!this.navMesh) return null;
    if (this.navMesh.isPointInMesh(point)) return point;

    // Duck-type shim satisfying navmesh's internal Vector2 API.
    // The library calls distance(), equals(), angle(), add(), subtract(), and clone()
    // on the passed point during projectPointToPolygon — plain {x,y} lacks all of these.
    interface NavVec2 {
      x: number; y: number;
      distance(v: NavVec2): number;
      equals(v: NavVec2): boolean;
      angle(v: NavVec2): number;
      add(v: NavVec2): void;
      subtract(v: NavVec2): void;
      clone(): NavVec2;
    }
    const vec2: NavVec2 = {
      x: point.x,
      y: point.y,
      distance(v) { return Math.sqrt((this.x - v.x) ** 2 + (this.y - v.y) ** 2); },
      equals(v)   { return this.x === v.x && this.y === v.y; },
      angle(v)    { return Math.atan2(v.y - this.y, v.x - this.x); },
      add(v)      { this.x += v.x; this.y += v.y; },
      subtract(v) { this.x -= v.x; this.y -= v.y; },
      clone()     { return { ...this }; },
    };

    const result = this.navMesh.findClosestMeshPoint(vec2 as any, 64);
    return result.point as WorldPoint | null;
  }

  /** Build navmesh from pre-computed convex walkable polygons instead of a grid. */
  buildFromPolygons(walkablePolygons: WorldPoint[][]): void {
    try {
      this.navMesh = walkablePolygons.length > 0 ? new NavMesh(walkablePolygons) : null;
    } catch (e) {
      console.warn('NavmeshSystem: buildFromPolygons failed —', e);
      this.navMesh = null;
    }
  }

  get isReady(): boolean {
    return this.navMesh !== null;
  }
}
